/**
 * Column Mapping Modal Component
 *
 * Allows users to map Excel columns to database columns before import.
 * Provides intelligent suggestions and validates required field mappings.
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Select, Alert, Tag } from 'antd';
import './BulkImport.css';

const { Option } = Select;

const ColumnMappingModal = ({
  visible,
  onClose,
  onConfirm,
  previewData,
  tableSchema,
  tableName
}) => {
  const [columnMapping, setColumnMapping] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (previewData?.suggestedMapping) {
      setColumnMapping(previewData.suggestedMapping);
    }
  }, [previewData]);

  const handleMappingChange = (excelColumn, dbColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [excelColumn]: dbColumn
    }));
  };

  const validateMapping = () => {
    const errors = [];

    // Safely access columns with optional chaining and default to empty array
    const columns = tableSchema?.data?.columns || tableSchema?.columns || [];
    const requiredFields = columns
      .filter(col => col.required)
      .map(col => col.column_id);

    const mappedFields = Object.values(columnMapping);
    const missingFields = requiredFields.filter(field => !mappedFields.includes(field));

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.map(fieldId => {
        const col = columns.find(c => c.column_id === fieldId);
        return col ? col.name : fieldId;
      });
      errors.push(`Missing required fields: ${missingFieldNames.join(', ')}`);
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleConfirm = () => {
    if (validateMapping()) {
      onConfirm(columnMapping);
    }
  };

  const columns = [
    {
      title: 'Excel Column',
      dataIndex: 'excelColumn',
      key: 'excelColumn',
      width: 200,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Maps To',
      dataIndex: 'mapping',
      key: 'mapping',
      width: 250,
      render: (_, record) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Select database column"
          value={columnMapping[record.excelColumn]}
          onChange={(value) => handleMappingChange(record.excelColumn, value)}
          showSearch
          allowClear
          optionFilterProp="children"
        >
          {(tableSchema?.data?.columns || tableSchema?.columns || []).map(col => (
            <Option key={col.column_id} value={col.column_id}>
              {col.name}
              {col.required && <Tag color="red" style={{ marginLeft: 8 }}>Required</Tag>}
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Sample Data',
      dataIndex: 'sampleData',
      key: 'sampleData',
      ellipsis: true,
      render: (data) => (
        <span style={{ color: '#8c8c8c' }}>
          {data || <em>No data</em>}
        </span>
      )
    }
  ];

  const dataSource = previewData?.detectedColumns?.map(col => ({
    key: col.name,
    excelColumn: col.name,
    sampleData: col.sampleValue
  })) || [];

  return (
    <Modal
      title={`Column Mapping - ${tableName}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          Confirm & Import
        </Button>
      ]}
      width={900}
      className="column-mapping-table"
    >
      {validationErrors.length > 0 && (
        <Alert
          message="Validation Errors"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {validationErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          closable
          onClose={() => setValidationErrors([])}
          style={{ marginBottom: 16 }}
        />
      )}

      {previewData?.willCreateColumns && (
        <Alert
          message="New Columns Will Be Created"
          description={
            <div>
              <p>The following columns don't exist in the table and will be automatically created:</p>
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                {previewData.newColumnsToCreate.map(col => (
                  <li key={col.column_id}>
                    <strong>{col.name}</strong> ({col.data_type})
                  </li>
                ))}
              </ul>
            </div>
          }
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Alert
        message="Map Excel columns to database fields"
        description="Required fields must be mapped. Unmapped columns will be ignored during import."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {previewData?.dataQuality && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="Data Quality"
            description={
              <div>
                <p><strong>Total Rows:</strong> {previewData.totalRows}</p>
                <p><strong>Duplicate Rows:</strong> {previewData.dataQuality.duplicateRows}</p>
                <p><strong>Overall Null Percentage:</strong> {previewData.dataQuality.overallNullPercentage}%</p>
              </div>
            }
            type="warning"
            showIcon
          />
        </div>
      )}

      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        scroll={{ y: 400 }}
        bordered
      />
    </Modal>
  );
};

export default ColumnMappingModal;
